import React from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';

const ALL_PATH_NAV = [
  { text: 'My Learning', icon: <MenuBookIcon />, to: '/my-learning', paths: null, authRequired: true },
  { text: 'Cohort', icon: <GroupsIcon />, to: '/cohort', paths: null, authRequired: true },
  { text: 'Admin', icon: <AdminPanelSettingsIcon />, to: '/admin', paths: null, adminRequired: true },
  { text: 'About', icon: <InfoIcon />, to: '/about', paths: null },
];

const getNavItems = (learningPaths = [], isLoggedIn = false, role = 'learner') =>
  ALL_PATH_NAV.filter((item) => {
    if (item.authRequired && !isLoggedIn) return false;
    if (item.adminRequired && !['admin', 'platform_owner'].includes(role)) return false;
    return item.paths === null || item.paths.some((p) => learningPaths.includes(p));
  });

const Navigation = ({ open, onClose, onOpenSearch, onOpenOnboarding, onOpenAccount }) => {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  const navItems = getNavItems(user?.learning_paths, Boolean(user), user?.role);
  const isActiveNav = (item) =>
    location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(`${item.to}/`));

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 280, bgcolor: 'background.paper' } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Drawer header */}
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            component={RouterLink}
            to="/"
            aria-label="Qode Clarity home"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'text.primary',
              '&:hover': { opacity: 0.87 }
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Qode Clarity"
              sx={{
                display: 'block',
                width: 40,
                height: 40,
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: '1rem',
                lineHeight: 1,
                letterSpacing: 0
              }}
            >
              Qode Clarity
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => { onOpenSearch(); onClose(); }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose} aria-label="close menu">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {user && (
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Signed in as <strong>{user.first_name}</strong>
            </Typography>
            <Button
              size="small"
              startIcon={<AccountCircleIcon />}
              onClick={() => {
                onOpenAccount();
                onClose();
              }}
              sx={{ mt: 1, px: 0, textTransform: 'none', fontWeight: 700 }}
            >
              Account profile
            </Button>
          </Box>
        )}

        <Divider />

        {/* Nav items */}
        <List sx={{ flexGrow: 1, pt: 1 }}>
          {user && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onOpenOnboarding();
                  onClose();
                }}
                sx={{
                  py: 1.5,
                  px: 3,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                  <GroupsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="How this works"
                  secondary="Program guide and glossary"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItemButton>
            </ListItem>
          )}
          {navItems.map((item) => {
            const active = isActiveNav(item);
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.to}
                  onClick={onClose}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderLeft: active ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                    bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.14) : alpha(theme.palette.primary.main, 0.04),
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.14),
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: active ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: active ? 900 : 600 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ mt: 'auto' }} />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {new Date().getFullYear()} Qode Clarity
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Navigation;
